/**
 * PATH: packages/ui/src/contextMenu/ContextMenuSub.tsx
 * FILE_ID: UI-CTXMENU-POSITIONED-SUB-001
 * PURPOSE: The hover/tap-to-open submenu trigger + its portaled, clip-proof
 *          panel. Split out of PositionedContextMenu.tsx (change #269 D4
 *          500-line ceiling split).
 * ROLE IN ARCHITECTURE: Compound child of <ContextMenu> (ContextMenuRoot.tsx);
 *          reads the root's z-scale via MenuZIndexContext.
 * MAIN EXPORTS: ContextMenuSub, ContextMenuSubProps.
 * NON-RESPONSIBILITIES: Root panel geometry (ContextMenuRoot.tsx); plain
 *          rows/header/separator (ContextMenuPrimitives.tsx).
 * NOTES FOR FUTURE AI: The panel portals to document.body and is positioned
 *   FIXED from the trigger's bounding rect (BUG-2026-05-28-ALLOCATE-KANBAN-
 *   SUBMENU-CLIPPED) — no ancestor overflow rule can clip it. Placement math
 *   is the pure, unit-tested computeSubmenuPlacement; never re-derive it here.
 *   A null `left` is the left-flip. Map it to CSS `auto`, never `?? 0`
 *   (BUG-2026-09-24-ALLOCATE-KANBAN-SUBMENU-LEFT-PIN).
 */
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '../lib/utils';
import { computeSubmenuPlacement } from './contextMenuGeometry';
import {
  MenuZIndexContext,
  SUB_HOVER_CLOSE_DELAY_MS,
  menuRowBaseCls,
  type ContextMenuIcon,
} from './positionedShared';

export interface ContextMenuSubProps {
  label: string;
  icon?: ContextMenuIcon;
  /** Coloured swatch dot displayed in place of the icon (used by the
   *  kanban allocate submenu to surface board accent colours). */
  dot?: string;
  /** Disables the trigger row entirely — neither hover nor click opens
   *  the panel. Used while the parent has an in-flight mutation. */
  disabled?: boolean;
  /** Optional click handler on the TRIGGER row. Fires on plain click
   *  only — the hover-to-open flow does NOT call this. Used by rows
   *  where clicking quick-acts (allocate to default lane) while
   *  hovering reveals the full sub-list. */
  onClick?: () => void;
  /** Notify the parent when the panel opens or closes. Lets consumers
   *  lazy-load data on first open (e.g. lane lists) off the SAME hover
   *  signal that mounts the panel (BUG-2026-05-13-PINNED-BOARDROW-NO-LANES). */
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}

export function ContextMenuSub({
  label,
  icon: Icon,
  dot,
  disabled,
  onClick,
  onOpenChange,
  children,
}: ContextMenuSubProps) {
  const zIndexBase = useContext(MenuZIndexContext);
  const [open, setOpen] = useState(false);
  /** `true` while the cursor is over the trigger OR the panel — the
   *  close timer waits for this to stay false for the grace period
   *  before actually collapsing (hover-gap fix, BUG-2026-05-13). */
  const hoverHoldRef = useRef(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // BUG-2026-05-28-ALLOCATE-KANBAN-SUBMENU-CLIPPED: the panel is
  // portaled to document.body and positioned FIXED from the trigger's
  // bounding rect, so no ancestor overflow rule can clip it. Placement
  // (side flip, anchor-flip-up, dynamic maxHeight) comes from the pure
  // computeSubmenuPlacement helper.
  const [placement, setPlacement] = useState<{
    left: number | null;
    right: number | null;
    top: number;
    maxHeight: number;
  } | null>(null);

  // Notify the parent on every state flip so consumers can lazy-load
  // on first open (same shape as Radix's onOpenChange). Reset placement
  // when closing so the next open re-measures.
  useEffect(() => {
    onOpenChange?.(open);
    if (!open) setPlacement(null);
  }, [open, onOpenChange]);

  // Measure-then-place. Runs on open + resize + capture-phase scroll so
  // the panel tracks its trigger. First pass estimates with fallback
  // dimensions; the rAF re-run refines once the panel has painted.
  useLayoutEffect(() => {
    if (!open) return;
    const trigger = triggerRef.current;
    if (!trigger) return;
    function clamp() {
      if (!trigger) return;
      const panel = panelRef.current;
      const t = trigger.getBoundingClientRect();
      const pw = panel?.offsetWidth ?? 200;
      const ph = panel?.offsetHeight ?? 200;
      const next = computeSubmenuPlacement({
        triggerRect: { top: t.top, bottom: t.bottom, left: t.left, right: t.right },
        panelWidth: pw,
        panelHeight: ph,
        viewportWidth: window.innerWidth,
        viewportHeight: window.innerHeight,
        // Zero gap: the panel docks flush against the trigger so the
        // cursor never crosses dead space (hover-grace bridge).
        gap: 0,
      });
      setPlacement({
        left: next.left,
        right: next.right,
        top: next.top,
        maxHeight: next.maxHeight,
      });
    }
    clamp();
    const raf = requestAnimationFrame(clamp);
    window.addEventListener('resize', clamp);
    window.addEventListener('scroll', clamp, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', clamp);
      window.removeEventListener('scroll', clamp, true);
    };
  }, [open]);

  const cancelClose = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  const scheduleClose = useCallback(() => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      if (!hoverHoldRef.current) setOpen(false);
    }, SUB_HOVER_CLOSE_DELAY_MS);
  }, [cancelClose]);

  const handleEnter = useCallback(() => {
    if (disabled) return;
    hoverHoldRef.current = true;
    cancelClose();
    setOpen(true);
  }, [disabled, cancelClose]);

  const handleLeave = useCallback(() => {
    hoverHoldRef.current = false;
    scheduleClose();
  }, [scheduleClose]);

  // Touch / click toggle — hover doesn't fire on touch devices, so the
  // trigger needs an explicit tap-to-open. When `onClick` is supplied
  // AND the panel is NOT open, treat the click as a quick-action; when
  // the panel IS open, the trigger toggles it shut.
  const handleTriggerClick = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      e.stopPropagation();
      if (open) {
        setOpen(false);
        return;
      }
      if (onClick) {
        onClick();
        return;
      }
      setOpen(true);
    },
    [disabled, open, onClick]
  );

  const portalTarget = typeof document !== 'undefined' ? document.body : null;

  // Critical layout props INLINE for the same Tailwind-JIT-drift
  // immunity as the root panel (see the root's style comment).
  const panel =
    open && portalTarget
      ? createPortal(
          <div
            ref={panelRef}
            role="menu"
            // Marks this portaled panel as part of the parent menu's
            // logical scope for the root's outside-click/scroll detector.
            data-quotech-cmenu="panel"
            className="rounded-lg border border-border bg-card shadow-xl p-1"
            style={{
              position: 'fixed',
              // left:null is the left-flip signal (anchor on `right`), not
              // viewport x=0. Coercing it with ?? 0 pinned Allocate to Kanban
              // to the extreme left whenever the email card sat on the right.
              left: placement?.left ?? 'auto',
              right: placement?.right ?? 'auto',
              top: placement?.top ?? 0,
              zIndex: zIndexBase + 2,
              visibility: placement ? 'visible' : 'hidden',
              minWidth: 200,
              maxWidth: 320,
              // Dynamic cap from computeSubmenuPlacement — matches the
              // room actually available in the chosen anchor direction,
              // so an anchor-flipped-up panel never buries its trigger.
              maxHeight: placement ? `${placement.maxHeight}px` : '60vh',
              overflowY: 'auto',
              overscrollBehavior: 'contain',
            }}
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
            onClick={(e) => e.stopPropagation()}
          >
            {children}
          </div>,
          portalTarget
        )
      : null;

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <button
        ref={triggerRef}
        type="button"
        role="menuitem"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={handleTriggerClick}
        className={cn(
          menuRowBaseCls,
          'hover:bg-accent text-foreground justify-between',
          open && 'bg-accent',
          disabled && 'opacity-40 pointer-events-none'
        )}
      >
        <span className="flex items-center gap-2 min-w-0">
          {dot && (
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: dot }} />
          )}
          {Icon && !dot && <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
          <span className="truncate">{label}</span>
        </span>
        <span className="text-muted-foreground text-[10px] shrink-0 ml-2">&#9656;</span>
      </button>
      {panel}
    </div>
  );
}
